import React, { FC } from 'react'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import EarlyBirdModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/EarlyBirdModal'
import FlashSaleModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/FlashSaleModal'
import { GiveawayModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GiveawayModal'
import { GuildGiveawayModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GuildGiveawayModal'
import { NoFeesModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/NoFeesModal'
import { useTheatricalModalController } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useTheatricalModalController'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface Props {
  projectName: TheatricalReleaseObject['title']
  theatricalSlug: string
}

export const TicketPromoModals: FC<Props> = ({ theatricalSlug, projectName }) => {
  const { promo, isReady } = useTheatricalPromotionContext()
  const [isModalOpen, closeModal] = useTheatricalModalController(promo, theatricalSlug, isReady)
  const { promoType, discount, code } = promo ?? {}
  const { areGuildTicketsAvailable, loading: guildTicketsLoading } = useGuildTickets(theatricalSlug)
  const { query } = useSafeRouter()
  const isGiftFlow = query?.flow === 'guild-gift' || query?.flow === 'gift-tickets'

  if (!isReady || !promoType || isGiftFlow) return null

  if (promoType !== 'GIVEAWAY') {
    return (
      <>
        {promoType === 'FLASH_SALE' && (
          <FlashSaleModal theatricalName={projectName} open={isModalOpen} onClose={closeModal} discount={discount} />
        )}

        {promoType === 'EARLY_BIRD' && (
          <EarlyBirdModal
            theatricalSlug={theatricalSlug}
            onClose={closeModal}
            open={isModalOpen}
            code={code}
            discount={discount}
          />
        )}

        {promoType === 'NO_FEES' && !!code && (
          <NoFeesModal
            code={code}
            isManualCode
            open={isModalOpen}
            onClose={closeModal}
            theatricalSlug={theatricalSlug}
          />
        )}
      </>
    )
  }

  if (guildTicketsLoading) return null
  if (areGuildTicketsAvailable) {
    return <GuildGiveawayModal isOpen={isModalOpen} onClose={closeModal} theatricalSlug={theatricalSlug} />
  } else {
    return <GiveawayModal isOpen={isModalOpen} onClose={closeModal} theatricalSlug={theatricalSlug} />
  }
}

export default TicketPromoModals
